import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCandidateAuditLog } from '@/hooks/use-candidates';
import type { AuditEventType, AuditLogEntry } from '@/types/candidates';
import { formatDistanceToNow } from 'date-fns';
import { useEffect, useMemo, useRef } from 'react';
import {
  AUDIT_FILTERS,
  getEventIcon,
  renderEventDescription,
} from '../lib/audit-event-renderer';

interface AuditLogFeedProps {
  candidateId: string;
  filterKey: string;
  onFilterChange: (key: string) => void;
}

function initials(firstName: string, lastName: string): string {
  return `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase() || '?';
}

function EventRow({ entry }: { entry: AuditLogEntry }) {
  const Icon = getEventIcon(entry.eventType);
  const actorInitials = entry.actor
    ? initials(entry.actor.firstName, entry.actor.lastName)
    : 'S';

  return (
    <li className="flex items-start gap-3 py-3">
      <Avatar className="h-8 w-8 shrink-0 rounded-full">
        {entry.actor?.avatarUrl && <AvatarImage src={entry.actor.avatarUrl} />}
        <AvatarFallback className="rounded-full text-xs">{actorInitials}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="wrap-anywhere text-sm leading-relaxed text-foreground">
            {renderEventDescription(entry)}
          </p>
        </div>
        <p className="ml-6 mt-0.5 text-xs text-muted-foreground">
          {formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true })}
        </p>
      </div>
    </li>
  );
}

export function AuditLogFeed({ candidateId, filterKey, onFilterChange }: AuditLogFeedProps) {
  const filter = useMemo(
    () => AUDIT_FILTERS.find((f) => f.key === filterKey) ?? AUDIT_FILTERS[0],
    [filterKey]
  );
  const types: AuditEventType[] = filter.types;

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCandidateAuditLog(candidateId, types);

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const entries = data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-medium">Activity</h2>
        <Select value={filterKey} onValueChange={onFilterChange}>
          <SelectTrigger className="h-9 w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AUDIT_FILTERS.map((f) => (
              <SelectItem key={f.key} value={f.key}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-border p-6 text-sm text-muted-foreground">
          Loading...
        </div>
      ) : error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load audit log.
        </div>
      ) : entries.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-10 text-center">
          <p className="text-sm text-muted-foreground">
            No {filter.key === 'all' ? '' : filter.label.toLowerCase() + ' '}events yet.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-background">
          <ul className="divide-y divide-border px-5">
            {entries.map((e) => (
              <EventRow key={e.id} entry={e} />
            ))}
          </ul>
          <div ref={sentinelRef} className="px-5 py-3 text-center">
            {isFetchingNextPage ? (
              <span className="text-xs text-muted-foreground">Loading more...</span>
            ) : hasNextPage ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchNextPage()}
                className="text-xs"
              >
                Load more
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">End of log</span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
